import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Sup, Text } from "@onnit-js/ui/components/text";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import { Testimonial } from "@onnit-js/ui/components/module/testimonials";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import usePageData from "../../hooks/usePageData";
import SupplementFacts from "../../components/common/SupplementFacts";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import config from "../../config/config";

const transformProductName = (name: string): string => name.replace(/Melatonin Spray - /giu, "");

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    // Used to set current products supplement facts image.
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/melatonin`;
    const { IMG_PARAMS } = config;
    const mainColor = "#96B6A8";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    productNameTransform={transformProductName}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={1943}
                            height={873}
                            alt="Melatonin spray on a night stand"
                            src={[
                                `${IMG_URL}/2025-Melatonin-Wpage-Gallery-1b-Mint-920x920.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/2025-Melatonin-Wpage-Gallery-1b-Mint-1940x873.jpg${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            alt="woman sleeping"
                            src={`${IMG_URL}/2025-Melatonin-Wpage-How.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                        <Img
                            alt="bottle of lavender flavored melatonin on a white bedsheet"
                            src={`${IMG_URL}/MS-M-WMT-2022-LS-2.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <Box bg="#161616" mt={[0, 0, 6, 8]} py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                        <Box gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]} px={4}>
                            <Text as="h2" color="grays.3" typeStyle="text02" fontWeight="regular">
                                Melatonin Spray
                            </Text>
                        </Box>
                    </TwelveColumn>
                    <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                        <Box
                            gridColumn={["7 / span 6", "7 / span 6", "7 / span 6", "7 / span 5"]}
                            px={4}
                            mb={[6, 6, 0]}
                        >
                            <Img
                                src={`${IMG_URL}/2025-STS-Wpage-Product-Hero-Mint-1920x1080.jpg${IMG_PARAMS}`}
                                alt="melatonin spray on a night stand"
                            />
                        </Box>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 5"]}
                            display={["block", "block", "flex"]}
                            alignItems="center"
                            px={4}
                        >
                            <Text
                                display={["block", "block", "none"]}
                                as="h2"
                                color="grays.3"
                                typeStyle="text02"
                                fontWeight="regular"
                                my={3}
                            >
                                Melatonin Spray
                            </Text>

                            <Text as="h3" typeStyle="title01" color={mainColor} mb={2}>
                                Ready-to-go <Box as="br" />
                                Sleep Support <Box as="br" display={["none", "none", "block"]} />
                                Supplement
                                <Sup />
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn display={["block", "block", "grid"]}>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]}
                            px={4}
                            mb={[6, 6, 6, 8]}
                        >
                            <Text as="p" color="white" typeStyle="text02" fontWeight="regular" mb={4}>
                                Melatonin is naturally secreted by the pineal gland and plays an important role in the
                                body’s sleep-wake cycle.{" "}
                                <b>Melatonin can help people fall asleep faster and lengthen overall sleep times.</b>
                                <Sup /> Try it next time you travel!
                            </Text>
                        </Box>
                        <Box gridColumn={["2 / span 10", "2 / span 10", "2 / span 10", "3 / span 10"]} px={4} mb={6}>
                            <Text as="h3" typeStyle="title01" color={mainColor} mb={4}>
                                Melatonin <Box as="br" display={["block", "block", "none"]} />
                                Spray is for:
                            </Text>
                            <List>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    Those who want to fall asleep more quickly
                                    <Sup />
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    People who want to stay asleep longer
                                    <Sup />
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    People traveling across time zones
                                    <Sup />
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular">
                                    Shift workers who need support with circadian rhythms
                                    <Sup />
                                </ListItem>
                            </List>
                        </Box>
                    </TwelveColumn>
                </Box>

                <Box py={[6, 6, 6, 8]}>
                    <Testimonial
                        imgSrc={`${IMG_URL}/aubrey.png`}
                        quote="I do some of my best work at night, and as much as I try to limit blue light exposure, light entering my eyes is inevitable. Before I started using melatonin I would toss and turn in bed, counting down the hours until my alarm, which as we all know just compounds the problem. With melatonin in my arsenal, it’s like clockwork. I take it prior to when I want to sleep, and I easily drift off into dreamland.<br /><br />The spray format is a game changer — it is ready to go when I need it. Then in the morning when the light hits my eyes again, after a few minutes I feel recharged, resteFd, and ready to own the day."
                        author="Aubrey Marcus"
                        title="Onnit Founder"
                    />
                </Box>

                <ImageContentPortrait
                    reverse
                    image={(
                        <Img
                            src={`${IMG_URL}/2025-Melatonin-Wpage-Gallery-2-920x920.jpg${IMG_PARAMS}`}
                            alt="Bottle of lavender flavored melatonin with a woman sleeping in the background"
                        />
                    )}
                    content={(
                        <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                            <Text as="h3" typeStyle="title02" mb={[4, 4, 6]}>
                                How Melatonin Optimizes You
                            </Text>
                            <Text as="p" typeStyle="text02" fontWeight="regular">
                                Supplementing melatonin helps the body know when it is time to sleep, and it supports a
                                better night’s rest
                                <Sup />. Onnit’s spray comes in two delicious flavors that make supplementation fast and
                                enjoyable.
                            </Text>
                        </Box>
                    )}
                />

                <ImageContentPortrait
                    reverse
                    image={<Img src={`${IMG_URL}/2023-Melatonin-Wpage-Tips.jpg${IMG_PARAMS}`} aria-hidden="true" />}
                    content={(
                        <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                            <Text as="h3" typeStyle="title02" mb={[4, 4, 6]}>
                                Tips to improve sleep
                            </Text>
                            <List>
                                <ListItem typeStyle="text02" fontWeight="regular" mb={3}>
                                    Limit blue light exposure during nighttime hours. Use blue light filters on your
                                    phone and laptop when available.
                                </ListItem>
                                <ListItem typeStyle="text02" fontWeight="regular" mb={3}>
                                    Stay hydrated and mineralized. Himalayan salt is great to take before bed.
                                </ListItem>
                                <ListItem typeStyle="text02" fontWeight="regular" mb={3}>
                                    Use blackout curtains on windows, so that melatonin levels in your body stay high
                                    until you are ready to wake up.
                                </ListItem>
                                <ListItem typeStyle="text02" fontWeight="regular">
                                    Avoid high sugar foods before going to sleep to help maintain steady blood sugar and
                                    insulin levels.
                                </ListItem>
                            </List>
                        </Box>
                    )}
                />

                <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />}>
                    <>
                        <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" display="inline" mr={1}>
                            WARNING:
                        </Text>
                        <Text as="p" fontSize={0} display="inline" typeStyle="default" color="grays.4" mb={4}>
                            Consult a medical doctor before taking this or any other nutritional supplement if you are
                            pregnant, nursing, have or suspect a medical condition or are taking any
                            medications or if you are experiencing long-term sleep difficulties. Product may induce
                            sleep or cause drowsiness. Do not drive or operate machinery when taking melatonin.
                            Store in a cool, dry place. Do not use if freshness seal is missing or broken.{" "}
                            <span
                                className="bold"
                            >KEEP OUT OF REACH OF CHILDREN.
                            </span>
                        </Text>
                    </>
                </UsageAndDisclaimers>
            </>
        </ThemeProvider>
    );
}
